import { useCallback, useEffect, useRef, useState } from 'react';
import { useCustom, useDataProvider } from '@refinedev/core';
import type { BaseRecord } from '@refinedev/core';
import type { PageMeta } from '@/api/resources';

/**
 * One page of a cursor-paginated admin endpoint.
 *
 * ⚠️ **There is no total and no page number, here or anywhere.** The API is
 * cursor-paginated throughout (CLAUDE.md §5) and exposes no count, so "page 3 of
 * 12" would be inventing the 12. What this hook offers instead is a cursor
 * stack: `next()` pushes `meta.nextCursor`, `previous()` pops back to the cursor
 * that produced the page before, and `reset()` returns to the first page.
 * "Previous" is a walk back through cursors already visited rather than a
 * computed offset, because an opaque cursor cannot be decremented.
 *
 * ⚠️ The hook is driven through Refine's `custom` data-provider method, which is
 * what makes it testable: a stub `DataProvider` in a test drives exactly the
 * code path the browser does. `useCustom` unwraps to `result.data` holding the
 * parsed body — see `use-analytics.test.tsx` for why that is worth pinning.
 */

export interface CursorPage<T> {
  rows: T[] | undefined;
  meta: PageMeta | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
  /** Advance to `meta.nextCursor`. No-op when the server says there is no more. */
  next: () => void;
  /** Back to the previous cursor in the stack. */
  previous: () => void;
  /** Back to the first page. Called automatically when a filter changes. */
  reset: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  /** How many pages deep we are, for "page 3" wording that names no total. */
  pageNumber: number;
}

export type CursorQuery = Record<string, string | number | boolean | undefined>;

export function useCursorPage<T>(path: string, query: CursorQuery = {}): CursorPage<T> {
  const [stack, setStack] = useState<Array<string | null>>([null]);
  const cursor = stack[stack.length - 1] ?? null;

  // ⚠️ A filter change must return to the first page. Paging is a stack of
  // opaque cursors minted against the PREVIOUS filter set, so carrying one
  // across a filter change asks the server to continue a result set that no
  // longer exists — which reads as "the filter did nothing".
  const fingerprint = JSON.stringify(query);
  const previousFingerprint = useRef(fingerprint);
  useEffect(() => {
    if (previousFingerprint.current !== fingerprint) {
      previousFingerprint.current = fingerprint;
      setStack([null]);
    }
  }, [fingerprint]);

  const result = useCustom<BaseRecord>({
    url: path,
    method: 'get',
    config: { query: { ...query, ...(cursor ? { cursor } : {}) } },
    queryOptions: { retry: 0 },
  });

  const body = result.query.isSuccess
    ? (result.result.data as unknown as { data: T[]; meta: PageMeta } | undefined)
    : undefined;

  const meta = body?.meta;

  return {
    rows: body?.data,
    meta,
    isLoading: result.query.isPending,
    error: result.query.error,
    refetch: () => {
      void result.query.refetch();
    },
    next: () => {
      if (!meta?.hasMore || !meta.nextCursor) return;
      const nextCursor = meta.nextCursor;
      setStack((prev) => [...prev, nextCursor]);
    },
    previous: () => {
      setStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
    },
    reset: () => {
      setStack([null]);
    },
    hasPrevious: stack.length > 1,
    hasNext: Boolean(meta?.hasMore && meta.nextCursor),
    pageNumber: stack.length,
  };
}

/**
 * An imperative call through the data provider, for the things that are not a
 * list and not a Refine CRUD verb.
 *
 * `useCustomMutation` only accepts post/put/patch/delete, and the audited child
 * reveal is a **GET** carrying a required `reason` query parameter. Reaching for
 * `fetch` directly would work in the browser and be untestable in a spec, so
 * this goes through the same `DataProvider.custom` every other call uses and a
 * stub provider can observe it.
 */
export function useAdminRequest(): <T>(args: {
  url: string;
  method: 'get' | 'post' | 'patch' | 'delete';
  query?: CursorQuery;
  payload?: unknown;
}) => Promise<T> {
  const dataProvider = useDataProvider();

  return useCallback(
    async <T>({
      url,
      method,
      query,
      payload,
    }: {
      url: string;
      method: 'get' | 'post' | 'patch' | 'delete';
      query?: CursorQuery;
      payload?: unknown;
    }): Promise<T> => {
      const provider = dataProvider();
      if (!provider.custom) throw new Error('The data provider has no `custom` method.');
      const response = await provider.custom<BaseRecord>({ url, method, query, payload });
      return response.data as unknown as T;
    },
    [dataProvider],
  );
}
